import React from "react";
import { usePreviousFeed, oldMarketAt, oddsChangeClass } from "./oddsChange";
import { formatBook } from "../../../utils/formatOdds";

/**
 * BetTableSuperOver2 – market table of the five-cricket pages (superover, superover2, cricketv3).
 * Reference markup/logic (allpanel9 admin Market2 / Market6 / Market7 components):
 *   .market-container > .market-2 (Bookmaker, Back/Lay) + .market-6 (Fancy, No/Yes) + .market-6 (Fancy1, Back/Lay + Bet Lock)
 * - body gets .suspendedfull when the market gscode != 1
 * - a row gets .suspendedtext when the market is open (gscode 1) but the section gscode != 1; data-title = gstatus
 * - a price box gets .no-val when its odds are 0; the size line only shows when size and odds are non-zero
 * - sizes use the reference kval format (300000 -> 300K)
 *
 * API t2 array (by dtype): 2 → Bookmaker (gtype match1), 6 → Fancy, 7 → Fancy1.
 */

// Reference v-kval directive
const kval = (size) => {
  const n = Number(size);
  return n >= 1000 ? `${parseFloat((n / 1000).toFixed(1))}K` : String(parseFloat(n));
};

const sortBy = (list, key, desc = false) =>
  [...(list || [])].sort((a, b) => ((Number(a?.[key]) || 0) - (Number(b?.[key]) || 0)) * (desc ? -1 : 1));

const NO_MARKETS = [];

const BetTableSuperOver2 = ({ data = NO_MARKETS, onBetClick, exposures = {} }) => {
  // previous feed for the reference odds-change colours (.bg-success / .bg-danger)
  const oldData = usePreviousFeed(data);

  const getExposure = (selectionName) => {
    if (!selectionName) return 0;
    const keys = [
      selectionName,
      selectionName.trim(),
      selectionName.toLowerCase(),
      selectionName.toLowerCase().trim(),
      selectionName.toUpperCase(),
      selectionName.toUpperCase().trim(),
    ];
    for (const key of keys) {
      if (key && exposures[key] !== undefined) return exposures[key];
    }
    return 0;
  };

  const rowProps = (market, item) => {
    const suspended = Number(market?.gscode) === 1 && Number(item.gscode) !== 1;
    return { suspended, "data-title": item.gstatus, className: `bet-table-row${suspended ? " suspendedtext" : ""}` };
  };

  // One price box (.bl-box back|lay); clicking a live price keeps the existing bet-click behaviour
  const priceBox = (market, item, odd, side, extra = "", key, change = "") => {
    const odds = Number(odd?.odds) || 0;
    const size = Number(odd?.size) || 0;
    const clickable = odds !== 0 && !rowProps(market, item).suspended && Number(market?.gscode) === 1;
    return (
      <div
        key={key}
        className={`bl-box ${side}${extra}${odds === 0 ? " no-val" : ""}${change}`}
        onClick={clickable ? () => onBetClick?.(odds, item.nat, side, item) : undefined}
      >
        <span className="d-block odds">{odds === 0 ? "—" : odd.odds}</span>{" "}
        {size !== 0 && odds !== 0 ? <span className="d-block">{kval(size)}</span> : null}
      </div>
    );
  };

  const odd = (item, otype) => sortBy(item.odds?.filter((o) => o.otype === otype), "tno", true);

  // reference getClass(odds, otype, tno, sectionIndex) against the same market in the previous feed
  const changeOf = (oldMarket, idx, o, side) => oddsChangeClass(oldMarket, idx, side, o.tno, o.odds);

  const renderBookmaker = (market, index, oldMarket) => {
    const sections = sortBy(market.section, "sno");
    return (
      <div className="market-2">
        <div className="bet-table">
          <div className="bet-table-header">
            <div data-toggle="collapse" data-target={`#market${index}`} aria-expanded="true" className="nation-name">
              <span title={market.mname}>{market.mname}</span>
            </div>
          </div>{" "}
          <div
            id={`market${index}`}
            data-title={market.status}
            className={`bet-table-body collapse show${Number(market.gscode) !== 1 ? " suspendedfull" : ""}`}
          >
            <div className="bet-table-row">
              <div className="text-right nation-name">
                <span className="max-bet" />
              </div>{" "}
              <div className="back bl-title d-none-mobile">Back</div>{" "}
              <div className="lay bl-title d-none-mobile">Lay</div>
            </div>{" "}
            {sections.map((item, idx) => {
              const { suspended: _s, ...props } = rowProps(market, item);
              void _s;
              return (
                <div key={idx} {...props}>
                  <div className="nation-name d-none-mobile">
                    <p>{item.nat}</p>{" "}
                    <p className="mb-0 float-left" style={{ color: "red" }}>
                      {formatBook(getExposure(item.nat))}
                    </p>{" "}
                    <p className="mb-0 float-right d-none">0</p>
                  </div>{" "}
                  {odd(item, "back").map((o, i) => priceBox(market, item, o, "back", Number(o.tno) === 0 ? " back" : ` back${o.tno}`, `b${i}`, changeOf(oldMarket, idx, o, "back")))}{" "}
                  {sortBy(item.odds?.filter((o) => o.otype === "lay"), "tno").map((o, i) =>
                    priceBox(market, item, o, "lay", Number(o.tno) === 0 ? " lay" : ` lay${o.tno}`, `l${i}`, changeOf(oldMarket, idx, o, "lay")),
                  )}
                </div>
              );
            })}
          </div>
        </div>{" "}
        {market.rem ? <div className="remark">{market.rem}</div> : null}
      </div>
    );
  };

  // Fancy (No/Yes: lay first) and Fancy1 (Back/Lay + arrow and Bet Lock) share the market-6 markup
  const renderFancy = (market, index, isFancy1, oldMarket) => {
    const sections = sortBy(market.section, "sno");
    return (
      <div className="market-6">
        <div className="bet-table">
          <div className="bet-table-header">
            <div data-toggle="collapse" data-target={`#market${index}`} aria-expanded="true" className="nation-name">
              <span title={market.mname}>
                {isFancy1 && (
                  <a href="javascript:void(0)" title="">
                    <img src={`${import.meta.env.BASE_URL}img/arrow-down.svg`} className="mr-1" alt="" />
                  </a>
                )}
                {isFancy1 ? ` ${market.mname}` : market.mname}
              </span>
            </div>
            {isFancy1 && (
              <>
                {" "}
                <div className="float-right">
                  <a href="javascript:void(0)" className="btn btn-back">
                    Bet Lock
                  </a>
                </div>
              </>
            )}
          </div>{" "}
          <div
            id={`market${index}`}
            data-title={market.status}
            className={`bet-table-body collapse show${Number(market.gscode) !== 1 ? " suspendedfull" : ""}`}
          >
            <div className="bet-table-row">
              <div className="text-right nation-name" />{" "}
              {isFancy1 ? (
                <>
                  <div className="back bl-title d-none-mobile">Back</div>{" "}
                  <div className="lay bl-title d-none-mobile">Lay</div>
                </>
              ) : (
                <>
                  <div className="lay bl-title d-none-mobile">No</div>{" "}
                  <div className="back bl-title d-none-mobile">Yes</div>
                </>
              )}
            </div>{" "}
            {sections.map((item, idx) => {
              const { suspended: _s, ...props } = rowProps(market, item);
              void _s;
              const backs = odd(item, "back").map((o, i) => priceBox(market, item, o, "back", "", `b${i}`, changeOf(oldMarket, idx, o, "back")));
              const lays = odd(item, "lay").map((o, i) => priceBox(market, item, o, "lay", "", `l${i}`, changeOf(oldMarket, idx, o, "lay")));
              return (
                <React.Fragment key={idx}>
                  <div className="fancy-tripple">
                    <div {...props}>
                      <div className="nation-name d-none-mobile">
                        <p>{item.nat}</p>{" "}
                        <p className="mb-0" style={{ color: "red" }}>
                          {formatBook(getExposure(item.nat))}
                        </p>
                      </div>{" "}
                      {isFancy1 ? backs : lays} {isFancy1 ? lays : backs}
                    </div>
                  </div>{" "}
                  {item.rem ? <div className="sec-remark">{item.rem}</div> : null}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // Reference: markets in sno order (gtype "match" skipped); the index names #market{i}.
  // dtype 2 -> Bookmaker (Market2), 6 -> Fancy (Market6, No/Yes), 7 -> Fancy1 (Market7, Back/Lay + Bet Lock)
  const markets = sortBy(data, "sno");
  return (
    <div className="market-container">
      {markets.map((market, i) => {
        if (market.gtype === "match") return null;
        const dtype = Number(market.dtype);
        return (
          <React.Fragment key={market.mid ?? i}>
            {dtype === 2
              ? renderBookmaker(market, i, oldMarketAt(oldData, i))
              : dtype === 6 || dtype === 7
                ? renderFancy(market, i, dtype === 7, oldMarketAt(oldData, i))
                : null}{" "}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default BetTableSuperOver2;
